import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change, formValueSelector } from 'redux-form';
import includes from 'lodash-es/includes';
import isArray from 'lodash-es/isArray';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import some from 'lodash-es/some';
import get from 'lodash-es/get';
import moment from 'moment';

import {
  ControlTypeEnum,
  DictionariesEnum,
  FieldsEnum,
  FormsEnum,
  PaymentEnum,
  PaymentMethodEnum,
  PersonalDataAbTestEnum,
  QuotationStatusEnum,
  SectionsEnum,
} from 'app/shared/enums';
import { JsonPatchValueType } from 'app/shared/types/JsonPatchValue.type';
import { usePrevious } from 'app/shared/hooks/usePrevious';
import { useUser } from 'app/shared/hooks/useUser';
import { RootState } from 'app/store/rootReducer';
import {
  DictionarySetActionPayload,
  selectQuestion,
  getQuestionError,
  getVisibleDictionaryWithSubLabels,
  isQuestionValidSelector,
  QuestionErrorsClearActionPayload,
  RequirabilitySetActionPayload,
  FormsConfigActions,
  ShouldAutofillSetPayload,
  VisibilitySetActionPayload,
  getShouldAutofill,
  SetupValidatorForTypeActionPayload,
  ValidateFieldActionPayload,
  scrollToNextQuestion,
  validateField,
  selectIsMubiLeasingConfig,
  selectMismatchedCepikDataQuestionKeys,
  selectPreviousRegistrationNumber,
} from 'app/store/data/formsConfig';
import {
  getQuotationStatus,
  QuotationActions,
  selectQuotation,
} from 'app/store/data/quotation';
import { useIsPersonalDataTwoAdvantagesView } from 'app/shared/hooks/useIsPersonalDataTwoAdvantagesView';
import { updateQuotation } from 'app/store/data/quotationConfig';
import { getParamValues } from 'app/store/data/form';
import { selectIsQuestionVisible } from 'app/store/selectors/isQuestionVisible';
import { selectIsCepikModalSavingInProgress } from 'app/store/data/shortPathData';
import { clearCepikMergeResult } from 'app/store/thunks/clearCepikMergeResult';
import { runBusinessFunction } from 'app/services/businessFunctions.service';
import store from 'app/store';
import {
  changeFieldValue,
  clearFieldValue,
  clearVin,
  getFormValue,
  getSection,
  setQuestionIgnoreAnonymized,
} from 'app/services/businessFunctionHelpers';
import { parseCepikMergeResultKey } from 'app/shared/helpers/parseCepikMergeResultKey';
import { ApiService, RequestParams } from 'app/services/api.service';
import {
  Dictionary,
  DictionaryEntry,
  DictService,
} from 'app/services/dict.service';
import { peselFieldsList } from 'app/services/pesel.service/peselsKeys';
import { QuotationService } from 'app/services/quotation.service';
import { getCapitalized } from 'app/shared/helpers/getCapitalized';
import { getAutofilledStreet } from 'app/shared/helpers/getAutofilledStreet';
import { useCepikFormRowContainer } from 'app/components/CepikFormRowContainer/useCepikFormRowContainer';
import { useCurrentQuestionContext } from 'app/components/FormSection/CurrentQuestion.context';
import { FormRowProps } from './FormRow.interfaces';
import { dateFieldsToValidate } from './dateFieldsToValidate';
import { reportMessage } from 'app/services/errorReporter.service';

export function useFormRow({
  questionKey,
  inputClasses,
  formName,
  disablePatch,
  tagSectionName,
  cepikModalControl,
}: FormRowProps) {
  const dispatch = useDispatch();
  const { setCurrentQuestionKey } = useCurrentQuestionContext();
  const { isOperator } = useUser();
  const question = useSelector((state: RootState) =>
    selectQuestion({ state, formName, questionKey })
  );
  const dictionary = useSelector((state: RootState) =>
    getVisibleDictionaryWithSubLabels(
      state,
      formName,
      `${questionKey}_dictionary`,
      question.answersDictionaryKey
    )
  );
  const quotationStatus = useSelector(getQuotationStatus);
  const { id: quotationId, startDate } = useSelector(selectQuotation);
  const isTwoAdvantagesView = useIsPersonalDataTwoAdvantagesView();
  const fieldValue = useSelector((state: RootState) =>
    formValueSelector(formName)(state, questionKey)
  );
  const previousRegistrationNumber = useSelector(
    selectPreviousRegistrationNumber
  );

  const getFieldValue = useCallback(
    (field: string) => {
      const state = store.getState();
      return formValueSelector(formName)(state, field);
    },
    [formName]
  );

  const isQuestionValid = useSelector((state: RootState) =>
    isQuestionValidSelector(state, questionKey, formName)
  );
  const error = useSelector((state: RootState) =>
    getQuestionError(state, questionKey, formName)
  );
  const dependsOnValue = useSelector((state: RootState) =>
    formValueSelector(
      questionKey === FieldsEnum.CarOwnerPurchaseYear &&
        formName === FormsEnum.CompareCepikDataForm
        ? FormsEnum.CalculationForm
        : formName
    )(state, question.dependsOn ? question.dependsOn[0] : '')
  );
  const paramValues = useSelector((state: RootState) =>
    getParamValues(state, formName, question.answersDictionaryParams || [])
  );
  const shouldAutofill = useSelector((state: RootState) =>
    getShouldAutofill({ state, formName })
  );
  const isQuestionVisible = useSelector((state: RootState) =>
    selectIsQuestionVisible({
      state,
      formName,
      questionKey,
    })
  );
  const isMubiLeasingConfig = useSelector(selectIsMubiLeasingConfig);
  const isCepikModalSavingInProgress = useSelector(
    selectIsCepikModalSavingInProgress
  );
  const mismatchedCepikDataQuestionKeys = useSelector(
    selectMismatchedCepikDataQuestionKeys
  );

  const scrollToNextQuestionAction = useCallback(
    () => dispatch(scrollToNextQuestion(formName, questionKey)),
    [dispatch, formName, questionKey]
  );
  const setDictionary = useCallback(
    (payload: DictionarySetActionPayload) =>
      dispatch(FormsConfigActions.setDictionaryAction(payload)),
    [dispatch]
  );
  const setShouldAutofill = useCallback(
    (payload: ShouldAutofillSetPayload) =>
      dispatch(FormsConfigActions.setShouldAutofillAction(payload)),
    [dispatch]
  );
  const clearQuestionErrors = useCallback(
    (payload: QuestionErrorsClearActionPayload) =>
      dispatch(FormsConfigActions.clearQuestionErrorsAction(payload)),
    [dispatch]
  );
  const changeValue = useCallback(
    (field: string, value: string | number) =>
      dispatch(change(formName, field, value)),
    [formName, dispatch]
  );
  const setQuotationStatus = (value: QuotationStatusEnum) =>
    dispatch(QuotationActions.setQuotationStatus(value));
  const setQuestionVisibility = useCallback(
    (payload: VisibilitySetActionPayload) =>
      dispatch(FormsConfigActions.setQuestionVisibilityAction(payload)),
    [dispatch]
  );
  const setQuestionRequirability = (payload: RequirabilitySetActionPayload) =>
    dispatch(FormsConfigActions.setQuestionRequirabilityAction(payload));
  const setupValidatorForType = (payload: SetupValidatorForTypeActionPayload) =>
    dispatch(FormsConfigActions.setupValidatorForType(payload));
  const validateFieldAction = (payload: ValidateFieldActionPayload) =>
    dispatch(validateField(payload));
  const updateQuotationAction = (
    modelPath: string,
    value: JsonPatchValueType
  ) => dispatch(updateQuotation(modelPath, value));

  // workaround due to replacing componentWillReceiveProps/componentDidUpdate with useEffect
  const firstDependsOnValueUpdate = useRef(true);
  const firstIsQuestionVisibleUpdate = useRef(true);
  const firstFieldValueUpdate = useRef(true);
  const firstComponentUpdate = useRef(true);

  const isMounted = useRef(false);
  const shouldPatchInternal = useRef(false);
  const lastPatchedValue = useRef<string | number | boolean | undefined>(
    undefined
  );
  const prevFieldValue = usePrevious(fieldValue);
  const [lastFieldValue, setLastFieldValue] = useState<
    string | number | boolean | undefined
  >();
  const [dependsOnFieldValue, setDependsOnFieldValue] = useState<
    string | number | boolean | undefined
  >();
  const [isLoading, setIsLoading] = useState(false);
  const [changed, setChanged] = useState(false);
  const [wasAutoFilled, setWasAutoFilled] = useState(false);

  const {
    key,
    type,
    clearOnHide = true,
    dependsOn,
    answersDictionaryKey,
    dataUrl = '',
    modelPath,
    onChange,
    ignoreAnonymized,
    disabled,
    visible,
    onClick,
  } = question;

  const { hasAnyMergeResult } = useCepikFormRowContainer(key as FieldsEnum);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    lastPatchedValue.current = undefined;
  }, [quotationId]);

  useEffect(() => {
    if (!isMounted.current) return;

    initAnswers();
    setupValidatorForType({
      formName: formName,
      questionKey: question.key as FieldsEnum,
    });
    rememberDependsOnFieldValue();
    handleControlValueRestore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  useEffect(() => {
    if (key !== FieldsEnum.InsurantNip) return;

    handleControlValueRestore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  useEffect(() => {
    if (firstDependsOnValueUpdate.current || isCepikModalSavingInProgress) {
      firstDependsOnValueUpdate.current = false;
      return;
    }

    const shouldClearFieldValue =
      quotationStatus !== QuotationStatusEnum.Cleared &&
      (!key.includes('InsuranceHistory.claims.') || wasAutoFilled) &&
      (!cepikModalControl ||
        ![
          FieldsEnum.AdditionalDrivers1LicenseYear,
          FieldsEnum.Coowners1LegalAddressCity,
          FieldsEnum.Coowners2LegalAddressCity,
        ].includes(question.key as FieldsEnum)) &&
      dependsOnFieldValue;

    if (!dependsOnValue) {
      setDependsOnFieldValue(undefined);
    }

    if (shouldClearFieldValue) {
      clearFieldValue(key, formName, disablePatch);

      setDictionary({
        dictionaryKey: `${key}_dictionary`,
        dictionary: [],
        formName,
      });

      setWasAutoFilled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependsOnValue]);

  useEffect(() => {
    if (firstIsQuestionVisibleUpdate.current || isCepikModalSavingInProgress) {
      firstIsQuestionVisibleUpdate.current = false;
      return;
    }

    const shouldClearField = !isQuestionVisible && clearOnHide;

    if (!isQuestionVisible) {
      clearQuestionErrors({
        key,
        formName,
      });
    }

    if (shouldClearField) {
      clearFieldValue(key, FormsEnum.CalculationForm, disablePatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuestionVisible]);

  useEffect(() => {
    if (!question.required) {
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.required, question.type]);

  useEffect(() => {
    if (firstFieldValueUpdate.current) {
      firstFieldValueUpdate.current = false;
      return;
    }

    setLastFieldValue(prevFieldValue);

    if (
      peselFieldsList.includes(question.key as FieldsEnum) &&
      question?.onChange
    ) {
      question.onChange.forEach((functionName) =>
        runBusinessFunction(functionName, false)
      );
    }

    if (error?.visible) {
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  const onComponentUpdate = () => {
    if (firstComponentUpdate.current) {
      firstComponentUpdate.current = false;
      return;
    }

    const updateAnswers = () => {
      if (!dependsOn || !hasAnswers()) {
        return;
      }

      if (
        dependsOn.some((depend) =>
          dateFieldsToValidate.includes(depend as FieldsEnum)
        )
      ) {
        if (!moment(dependsOnValue, 'YYYY-MM-DD', true).isValid()) return;
      }

      const isDependsOnFieldValueChanged =
        dependsOnFieldValue !== dependsOnValue;

      const canAutofill =
        isNil(fieldValue) &&
        visible &&
        !isNil(dependsOnValue) &&
        !disabled &&
        type !== ControlTypeEnum.StreetName;

      if (canAutofill && dictionary && dictionary.length === 1) {
        const onlyKey = dictionary[0].key;
        changeValue(key, onlyKey);
        handleControlValueChange(onlyKey, true);
      }

      const hasICanNotFindMyCarEntry = (dictEntry: DictionaryEntry) =>
        [
          FieldsEnum.CarVersionEt,
          FieldsEnum.CarModelIe,
          FieldsEnum.CarVersionIe,
        ].includes(key as FieldsEnum) &&
        ['-1', 'no_car_type'].includes(dictEntry.key.toString());

      if (
        canAutofill &&
        dictionary &&
        dictionary.length === 2 &&
        dictionary.some(hasICanNotFindMyCarEntry)
      ) {
        const carEntry = dictionary.find(
          (dictEntry) => !hasICanNotFindMyCarEntry(dictEntry)
        );
        if (carEntry) {
          changeValue(key, carEntry.key);
          handleControlValueChange(carEntry.key, true);
        }
      }

      if (
        !isDependsOnFieldValueChanged ||
        !hasParamValuesDefined(JSON.parse(paramValues))
      ) {
        return;
      }

      rememberDependsOnFieldValue();

      fetchAnswers(answersDictionaryKey as string, JSON.parse(paramValues), {
        shouldOverwriteFilledValue: true,
      });
    };

    updateAnswers();

    if (
      peselFieldsList.includes(question.key as FieldsEnum) &&
      shouldPatchInternal.current &&
      !getIsInvalid()
    ) {
      patchQuotation(fieldValue);
    }

    if (
      [
        FieldsEnum.OwnerBirthDate,
        FieldsEnum.Coowners1BirthDate,
        FieldsEnum.Coowners2BirthDate,
      ].includes(question.key as FieldsEnum) &&
      question.visible &&
      !isNil(fieldValue)
    ) {
      validate();
    }
  };

  const getIsInvalid = useCallback(() => {
    return error && error.visible;
  }, [error]);

  const initAnswers = () => {
    const dictionaryKey = answersDictionaryKey as string;

    if (dataUrl) {
      setDictionaryFromDataUrl();

      return;
    }

    if (!hasAnswers()) {
      return;
    }

    const paramsValue = JSON.parse(paramValues);
    if (!dependsOn) {
      fetchAnswers(dictionaryKey);
    } else if (hasParamValuesDefined(paramsValue)) {
      fetchAnswers(dictionaryKey, paramsValue);
    }
  };

  const setDictionaryFromDataUrl = () => {
    ApiService.get(dataUrl).then(({ data }) => {
      if (data.length) {
        setDictionary({
          dictionaryKey: `${key}_dictionary`,
          dictionary: data,
          formName,
        });
      } else {
        setQuestionRequirability({
          key,
          required: false,
          formName,
        });
        setQuestionVisibility({
          key,
          visible: false,
          formName,
        });
      }
    });
  };

  const hasAnswers = useCallback(() => {
    return answersDictionaryKey !== null && answersDictionaryKey !== undefined;
  }, [answersDictionaryKey]);

  const hasParamValuesDefined = (paramValues: {
    [key: string]: string | number;
  }) => {
    return (
      !isEmpty(paramValues) &&
      !includes(paramValues, undefined) &&
      !includes(paramValues, null)
    );
  };

  const getSelectedEntry = (data: Array<DictionaryEntry>) => {
    return data.find((entry) => Boolean(entry.selected));
  };

  const matchTypeOfVariable = (
    dictionary: Dictionary,
    value: string | number | boolean
  ) => {
    if (!isEmpty(dictionary)) {
      const firstEntry = dictionary[0].key;
      if (typeof firstEntry === 'number' && typeof value === 'string') {
        return parseInt(value);
      }
    }
    return value;
  };

  const patchQuotation = useCallback(
    (value: string | number | boolean) => {
      if (
        isOperator &&
        hasAnyMergeResult &&
        ![
          FormsEnum.CompareCepikDataForm,
          FormsEnum.CheckoutPaymentForm,
          FormsEnum.RecoveryRequestForm,
        ].includes(formName)
      ) {
        const excludedFields = [
          FieldsEnum.CheckoutPromotionId,
          FieldsEnum.CheckoutSkipFraudDetection,
          FieldsEnum.CheckoutPaymentDiscountCode,
        ];

        if (
          changed &&
          [
            FieldsEnum.CarRegistrationNumber,
            FieldsEnum.CarRegistrationNumberNotKnown,
            FieldsEnum.OwnerBirthDate,
            FieldsEnum.Coowners1BirthDate,
            FieldsEnum.Coowners2BirthDate,
          ].includes(questionKey as FieldsEnum)
        ) {
          dispatch(clearCepikMergeResult(quotationId));
        } else if (
          !excludedFields.includes(questionKey as FieldsEnum) &&
          !questionKey.includes('consent.') &&
          question.shouldPatch &&
          question?.visible
        ) {
          const value = !(isNil(fieldValue) || fieldValue === '');

          QuotationService.setCepikMergeResult(quotationId, [
            { key: key as FieldsEnum, value },
          ]);

          dispatch(
            QuotationActions.setQuotationQuestion(
              `cepikMergeResult.${parseCepikMergeResultKey(key)}`,
              value
            )
          );
        }
      }

      if (changed && questionKey === FieldsEnum.CarRegistrationNumber) {
        if (
          previousRegistrationNumber &&
          !previousRegistrationNumber[quotationId]
        )
          return;

        clearVin();
      }

      if (question.shouldPatch === false) {
        if (question.storeLocally && modelPath && value !== undefined) {
          dispatch(QuotationActions.setQuotationQuestion(modelPath, value));
        }
        return;
      }

      if (value !== undefined) {
        lastPatchedValue.current = value;
        updateQuotationAction(modelPath, value);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [question, modelPath, changed, hasAnyMergeResult, fieldValue]
  );

  const setInfoEkspertFieldsVisibility = useCallback(
    (visible: boolean) => {
      const shouldHideInfoEkspert =
        process.env.REACT_APP_HIDE_INFO_EKSPERT === 'true';

      if (
        [FieldsEnum.CarModelIe, FieldsEnum.CarVersionIe].includes(
          key as FieldsEnum
        ) &&
        shouldHideInfoEkspert
      ) {
        setQuestionVisibility({
          key,
          visible,
          formName,
        });
      }
    },
    [formName, key, setQuestionVisibility]
  );

  const rememberDependsOnFieldValue = useCallback(() => {
    if (dependsOn) {
      setDependsOnFieldValue(dependsOnValue);
    }
  }, [dependsOn, dependsOnValue]);

  const handleControlValueRestore = () => {
    if (fieldValue === undefined || fieldValue === null || !onChange) {
      return;
    }

    setCurrentQuestionKey(question.key);
    onChange.forEach((functionName) => runBusinessFunction(functionName, true));
  };

  const validate = useCallback(() => {
    if (
      (ignoreAnonymized &&
        fieldValue &&
        fieldValue.toString().includes('***')) ||
      disabled
    ) {
      return;
    }

    setTimeout(
      () =>
        validateFieldAction({
          value: getFieldValue(key),
          questionKey: key as FieldsEnum,
          formName,
          formSection: tagSectionName,
        }),
      0
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ignoreAnonymized,
    fieldValue,
    disabled,
    key,
    formName,
    tagSectionName,
    getFieldValue,
  ]);

  const handleControlValueChange = useCallback(
    (
      value: string | boolean | number,
      validate?: boolean,
      shouldPatch: boolean = !disablePatch,
      shouldRunOnChanges: boolean = true
    ) => {
      if (key === FieldsEnum.CarProductionYear) {
        setShouldAutofill({
          formName: formName,
          shouldAutofill: true,
        });
      }

      if (ignoreAnonymized) {
        setQuestionIgnoreAnonymized({
          key,
          formName,
          ignoreAnonymized: false,
        });
      }

      if (quotationStatus === QuotationStatusEnum.Cleared) {
        setQuotationStatus(QuotationStatusEnum.Created);
      }

      if (validate) {
        validateFieldAction({
          value,
          questionKey: key as FieldsEnum,
          formName,
        });
      }

      if (fieldValue !== value && !changed) {
        setChanged(true);
      }
      if (peselFieldsList.includes(questionKey as FieldsEnum)) {
        shouldPatchInternal.current = shouldPatch;
      } else if (shouldPatch && fieldValue !== value) {
        patchQuotation(value);
      }

      const makeScroll = () => {
        if (shouldPatch && fieldValue !== value) {
          setTimeout(() => {
            scrollToNextQuestionAction();
          });
        }
      };

      if (!shouldRunOnChanges || !onChange) {
        makeScroll();
        return;
      }

      setCurrentQuestionKey(question.key);
      onChange.forEach((functionName) =>
        runBusinessFunction(functionName, false)
      );
      makeScroll();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formName,
      key,
      ignoreAnonymized,
      quotationStatus,
      fieldValue,
      changed,
      questionKey,
      disablePatch,
      patchQuotation,
      onChange,
    ]
  );

  const handleControlBlur = useCallback(
    (
      shouldPatch: boolean = !disablePatch,
      shouldValidate: boolean = true,
      shouldRunOnChanges: boolean = false,
      skipScrollOnBlur?: boolean,
      value?: string | boolean | number
    ) => {
      if (shouldValidate) {
        validate();
      }

      if (
        changed &&
        mismatchedCepikDataQuestionKeys?.includes(questionKey as FieldsEnum)
      ) {
        dispatch(
          FormsConfigActions.removeMismatchedCepikDataQuestionKey(
            questionKey as FieldsEnum
          )
        );
      }

      if (peselFieldsList.includes(questionKey as FieldsEnum)) {
        shouldPatchInternal.current = shouldPatch;
      } else if (shouldPatch) {
        const valueToPatch = value || fieldValue;

        if (
          valueToPatch === undefined ||
          valueToPatch !== lastPatchedValue.current
        ) {
          patchQuotation(valueToPatch);
        }
      }

      const scrollOnBlur = () => {
        if (changed && !skipScrollOnBlur) {
          scrollToNextQuestionAction();
          setChanged(false);
        }
      };

      if (!shouldRunOnChanges || !onChange) {
        if (shouldPatch) {
          scrollOnBlur();
        }
        return;
      }

      onChange.forEach((functionName) =>
        runBusinessFunction(functionName, false)
      );
      scrollOnBlur();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      validate,
      questionKey,
      formName,
      disablePatch,
      changed,
      fieldValue,
      patchQuotation,
      onChange,
    ]
  );

  const handleControlValueClick = useCallback(() => {
    if (!onClick) {
      return;
    }

    setCurrentQuestionKey(question.key);
    onClick.forEach((functionName) => runBusinessFunction(functionName, false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClick]);

  const getInputClasses = useCallback(() => {
    const result = get(inputClasses, question.type);
    return result;
  }, [inputClasses, question.type]);

  const autofillField = useCallback(
    (dictionary: Dictionary) => {
      const lastValue =
        typeof lastFieldValue === 'number'
          ? (lastFieldValue as number).toString()
          : lastFieldValue;

      const canNotFindCarModelOrVersion =
        lastFieldValue &&
        !['-1', 'no_car_type'].includes(lastFieldValue.toString());

      if (
        key.includes('InsuranceHistory.claims.') &&
        isNil(fieldValue) &&
        dictionary.length === 1
      ) {
        const value = dictionary[0].key;
        changeFieldValue(key, value, formName);
        patchQuotation(value);
        setWasAutoFilled(true);
      }

      if (
        !fieldValue &&
        lastFieldValue !== undefined &&
        shouldAutofill &&
        canNotFindCarModelOrVersion
      ) {
        const isInDictionary = some(
          dictionary,
          (dictEntry) => dictEntry.key.toString() === lastValue
        );
        const carSection = getSection(
          isMubiLeasingConfig
            ? SectionsEnum.MubiLeasingSectionCar1
            : SectionsEnum.Car1,
          formName
        );

        if (
          dependsOn &&
          isInDictionary &&
          carSection?.questions.includes(key)
        ) {
          const lastQuestionFieldValue = getFormValue(
            dependsOn[0] as FieldsEnum,
            formName
          );

          if (lastQuestionFieldValue) {
            changeFieldValue(
              key,
              matchTypeOfVariable(dictionary, lastFieldValue),
              formName
            );
            patchQuotation(lastFieldValue);
            scrollToNextQuestionAction();
          }
        } else {
          setShouldAutofill({
            formName: formName,
            shouldAutofill: false,
          });
        }
      }
    },
    [
      dependsOn,
      fieldValue,
      formName,
      key,
      lastFieldValue,
      patchQuotation,
      scrollToNextQuestionAction,
      setShouldAutofill,
      shouldAutofill,
      isMubiLeasingConfig,
    ]
  );

  const modifyDictionary = useCallback(
    (data: Dictionary) => {
      if (question.key === FieldsEnum.PersonalDataPath && isTwoAdvantagesView) {
        return data.map((entry) => {
          const stringKey = entry.key.toString();
          if (stringKey === 'true') {
            return {
              ...entry,
              value: PersonalDataAbTestEnum.PersonalDataPathYes,
            };
          }
          if (stringKey === 'false') {
            return {
              ...entry,
              value: PersonalDataAbTestEnum.PersonalDataPathNo,
            };
          }
          return entry;
        });
      }

      if (question.answersDictionaryKey === DictionariesEnum.PaymentMethod) {
        const normalValue = PaymentEnum.OnlineOrTraditionalTransfer;
        const value = (() => {
          if (!startDate) return normalValue;

          const start = moment(startDate);
          if (!start.isValid()) return normalValue;

          const diffDays = start
            .startOf('day')
            .diff(moment().startOf('day'), 'days');

          return diffDays >= 0 && diffDays <= 4
            ? PaymentEnum.FastOnlineTransfer
            : normalValue;
        })();

        if (value !== PaymentEnum.OnlineOrTraditionalTransfer) {
          return data.map((entry) => {
            if (entry.key === PaymentMethodEnum.OnlineTransfer) {
              return { ...entry, value };
            }

            return entry;
          });
        }
      }

      const destinationCountryCodes = getFieldValue(
        FieldsEnum.TravelDestinationCountryCodes
      );

      if (
        question.key === FieldsEnum.TravelTransportForms &&
        formName === FormsEnum.TravelForm &&
        destinationCountryCodes?.includes('US')
      ) {
        return data.filter((entry) => entry.key !== 'own_car');
      }

      const isCarInsurersDictionary =
        question.answersDictionaryKey === DictionariesEnum.CarInsurers;
      const isLeased = Boolean(getFieldValue(FieldsEnum.LegalStatusLeased));

      if (isCarInsurersDictionary && isLeased) {
        const exists = data.some(
          (entry) => entry.key === 'no_insurance_new_car'
        );
        if (!exists) {
          return [
            ...data,
            {
              key: 'no_insurance_new_car',
              value: 'Brak ubezpieczyciela, auto nowe',
            },
          ];
        }
      }

      if (
        question.key === FieldsEnum.InexperiencedAdditionalDriverLicenseYear
      ) {
        const startDateYear = moment(startDate).year();
        const minYear = startDateYear - 3;
        const entriesToHide = data
          .filter((entry) => +entry.key < minYear)
          .map((entry) => entry.key);

        dispatch(
          FormsConfigActions.setDictionaryEntriesVisibilityAction({
            dictionaryKey: `driver_license_issue_years_${FieldsEnum.InexperiencedAdditionalDriverLicenseYear}`,
            entriesKey: entriesToHide,
            visible: false,
            formName: FormsEnum.CheckoutForm,
          })
        );
      }

      return data;
    },
    [
      question.key,
      question.answersDictionaryKey,
      formName,
      isTwoAdvantagesView,
      getFieldValue,
      startDate,
      dispatch,
    ]
  );

  const fetchAnswers = useCallback(
    (
      dictionaryKey: string,
      params?: RequestParams,
      options?: { shouldOverwriteFilledValue?: boolean }
    ) => {
      if (isCepikModalSavingInProgress) return;

      setIsLoading(true);

      DictService.getDictionary(dictionaryKey, params)
        .then(({ data }) => {
          const didDictParamsChange =
            params && JSON.stringify(params) !== paramValues;
          if (didDictParamsChange) {
            return;
          }

          const modifiedDictionary = modifyDictionary(data);

          const hasValue =
            !isNil(fieldValue) && fieldValue !== '' && !isArray(fieldValue);

          const canNotPassOwnValue = ![
            ControlTypeEnum.Name,
            ControlTypeEnum.StreetName,
          ].includes(question.type as ControlTypeEnum);

          if (
            hasValue &&
            canNotPassOwnValue &&
            !modifiedDictionary.some(({ key }) => key === fieldValue) &&
            !(
              question.key === 'InsuranceHistory.claims.' &&
              isEmpty(modifiedDictionary)
            ) &&
            question.key !== FieldsEnum.BankId
          ) {
            clearFieldValue(question.key, formName, disablePatch);
          }

          const selectedEntry = getSelectedEntry(modifiedDictionary);
          if (!isMounted.current) {
            return;
          }
          setIsLoading(false);

          setDictionary({
            dictionaryKey: `${question.key}_dictionary`,
            dictionary: modifiedDictionary,
            formName,
          });

          if (question.type === ControlTypeEnum.StreetName && params?.city) {
            const currentStreet = getFieldValue(question.key);
            const autofilledStreet = getAutofilledStreet(
              modifiedDictionary,
              params.city as string
            );
            const canOverwriteStreet =
              !currentStreet || Boolean(options?.shouldOverwriteFilledValue);

            if (
              autofilledStreet &&
              autofilledStreet !== currentStreet &&
              canOverwriteStreet
            ) {
              clearQuestionErrors({ key: question.key, formName });
              changeValue(question.key, autofilledStreet);
              validateFieldAction({
                value: autofilledStreet,
                questionKey: question.key as FieldsEnum,
                formName,
              });
              if (question.shouldPatch) {
                patchQuotation(autofilledStreet);
              }
            }
          }

          autofillField(modifiedDictionary);

          if (
            selectedEntry &&
            !fieldValue &&
            fieldValue !== false &&
            formName !== FormsEnum.CompareCepikDataForm
          ) {
            const { key, onChange = [] } = question;
            clearQuestionErrors({ key, formName });
            changeValue(key, selectedEntry.key);
            if (question.shouldPatch && fieldValue !== selectedEntry.key) {
              patchQuotation(selectedEntry.key);
            }
            onChange.forEach((functionName) =>
              runBusinessFunction(functionName, false)
            );
          }
          setInfoEkspertFieldsVisibility(!selectedEntry);
        })
        .catch((e) => {
          reportMessage(
            `DictService.getDictionary ${dictionaryKey}, ${JSON.stringify(
              params
            )}, ${e}`
          );
          if (!isMounted.current) {
            return;
          }
          setIsLoading(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      autofillField,
      changeValue,
      clearQuestionErrors,
      disablePatch,
      fieldValue,
      formName,
      paramValues,
      patchQuotation,
      question,
      setDictionary,
      setInfoEkspertFieldsVisibility,
      isCepikModalSavingInProgress,
      modifyDictionary,
    ]
  );

  const parseFunction = useCallback(
    (value: string) => {
      if (
        value &&
        [ControlTypeEnum.Name, ControlTypeEnum.LastName].includes(type)
      ) {
        return getCapitalized(value);
      }

      return value;
    },
    [type]
  );

  const shouldUseParse = [
    ControlTypeEnum.Name,
    ControlTypeEnum.LastName,
  ].includes(type as ControlTypeEnum);

  useEffect(onComponentUpdate, [
    question.key,
    getIsInvalid,
    fieldValue,
    patchQuotation,
    question.visible,
    validate,
    answersDictionaryKey,
    changeValue,
    dependsOn,
    dependsOnFieldValue,
    dependsOnValue,
    dictionary,
    disabled,
    fetchAnswers,
    handleControlValueChange,
    hasAnswers,
    key,
    type,
    paramValues,
    rememberDependsOnFieldValue,
    visible,
  ]);

  const isPersonalDataTooltipHidden =
    isTwoAdvantagesView &&
    [
      FieldsEnum.OwnerName,
      FieldsEnum.OwnerLastName,
      FieldsEnum.OwnerPesel,
      FieldsEnum.Coowners1Name,
      FieldsEnum.Coowners1LastName,
      FieldsEnum.Coowners1Pesel,
      FieldsEnum.Coowners2Name,
      FieldsEnum.Coowners2LastName,
      FieldsEnum.Coowners2Pesel,
    ].includes(question.key as FieldsEnum);

  return {
    isLoading,
    dependsOnFieldValue,
    handleControlValueChange,
    handleControlBlur,
    handleControlValueClick,
    getIsInvalid,
    getInputClasses,
    dictionary: dictionary || [],
    isQuestionValid,
    error,
    isQuestionVisible,
    question: isPersonalDataTooltipHidden
      ? { ...question, tooltip: '' }
      : question,
    shouldUseParse,
    parseFunction,
  };
}
